import {useHalState} from '@synako/haligator';
import {useApi, useClient, useMe} from '@/haligator-factory';

/**
 * Indique si l'utilisateur courant peut voir les marges (VM/TM/MK) et les catégories de prix
 * (Prix métier, Marché, Dérogation, etc.) sur les lignes produit.
 *
 * Basé sur la présence du lien `peut-voir-marges` dans le state du client,
 * filtré côté backend par halirun selon la permission `voir-les-marges`.
 */
export function usePeutVoirMarges(): boolean {
  const clientState = useHalState(useClient());
  return clientState.state?.hasOneLink('peut-voir-marges') ?? false;
}

/**
 * Indique si l'utilisateur courant peut voir la DLC estimée et le stock physique d'un article.
 *
 * Basé sur la présence du lien `peut-voir-dlc-stock` dans le state du client,
 * filtré côté backend par halirun selon la permission `voir-dlc-stock` via `relPermissions`.
 */
export function usePeutVoirDlcStock(): boolean {
  const clientState = useHalState(useClient());
  return clientState.state?.hasOneLink('peut-voir-dlc-stock') ?? false;
}

/**
 * Indique si l'utilisateur courant peut modifier le prix d'une ligne de panier
 * (édition inline en mode commercial).
 *
 * Basé sur la présence du lien `peut-modifier-prix` dans le state du client.
 */
export function usePeutModifierPrix(): boolean {
  const clientState = useHalState(useClient());
  return clientState.state?.hasOneLink('peut-modifier-prix') ?? false;
}

/**
 * Indique si l'utilisateur courant peut voir l'indicateur d'évolution de commande
 * d'un article (gagne/perdu/en-baisse/a-gagner) sur le bouton historique d'achat.
 *
 * Basé sur la présence du lien `peut-voir-evolution-article` dans le state du client,
 * filtré côté back par halirun selon la permission `voir-evolution-article` via `relPermissions`.
 */
export function usePeutVoirEvolutionArticle(): boolean {
  const clientState = useHalState(useClient());
  return clientState.state?.hasOneLink('peut-voir-evolution-article') ?? false;
}

/**
 * Indique si l'utilisateur courant peut consulter la configuration de prix Tolede
 * d'une ligne de panier (catégorie de tarif appliquée, tarifs, prix catalogue).
 *
 * Basé sur la présence du lien `peut-voir-configuration-prix-tolede` dans le state du client,
 * filtré côté backend par halirun selon la permission `voir-configuration-prix-tolede`.
 */
export function usePeutVoirConfigurationPrixTolede(): boolean {
  const clientState = useHalState(useClient());
  return (
    clientState.state?.hasOneLink('peut-voir-configuration-prix-tolede') ??
    false
  );
}

/**
 * Indique si l'utilisateur courant peut voir l'onglet "UB essentielles" dans `MesProduitsApp`.
 *
 * Basé sur la présence du lien `profils_ub` dans le state du client, filtré côté back
 * par halirun selon la permission `voir-les-ub`.
 */
export function usePeutVoirUBEssentielles(): boolean {
  const clientState = useHalState(useClient());
  return clientState.state?.hasOneLink('profils_ub') ?? false;
}

/**
 * Indique si l'utilisateur courant peut voir l'onglet "Toutes les conversations" (vue périmètre)
 * dans la popin des conversations (fiche client / prise de commande en mode commercial).
 */
export function usePeutVoirToutesLesConversations(): boolean {
  const meState = useHalState(useMe());
  return meState.state?.hasOneLink('conversations-perimetre') ?? false;
}

/**
 * Indique si l'utilisateur courant peut ouvrir FF Pilot (autologin Tolede).
 *
 * Basé sur le lien `ff-pilot` de Me : permission `ouvrir-ff-pilot`, code
 * utilisateur Tolede, et autologin configuré.
 */
export function usePeutOuvrirFFPilot(): boolean {
  const meState = useHalState(useMe());
  return meState.state?.hasOneLink('ff-pilot') ?? false;
}

/**
 * Code utilisateur effectif du viewer (impersonation via get-droits ; pour Synako c'est le
 * commercial sélectionné). Renvoie `undefined` pour un contact client : get-droits est réservé aux
 * utilisateurs FF/Synako (permission `voir-mes-droits`), donc le lien est absent.
 *
 * Sert à l'alignement des bulles de conversation (mes messages à droite) et à la commande
 * "Rejoindre".
 */
export function useCodeUtilisateurEffectif(): string | undefined {
  const droitsState = useHalState(useApi().follow('get-droits'));
  return droitsState.data?.code_utilisateur;
}

/**
 * Autorisation Tolede à saisir une dérogation (`dero_autorisee` sur `get-droits`).
 * Faux si le lien est absent (contact client).
 * Passer `enabled: false` pour ne pas fetch (ex. hors saisie avant ajout).
 */
export function useDeroAutorisee(enabled = true): boolean {
  const droitsState = useHalState(useApi().follow('get-droits'), {
    disable: !enabled,
  });
  return droitsState.data?.dero_autorisee === true;
}
